import React from 'react';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorState, Pager, Select, Table, useToast,
} from '@parsina/framework';
import { Archive, Copy, FileText, History, Plus, Zap } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { EMPTY, isIrtCredit } from '../../lib/format';
import { num, pageCountOf } from '../../lib/aiPricingView';
import TaskDialog from '../../components/TaskDialog';
import ActorLabel from '../../components/ActorLabel';
import { ARCHIVE_POLICY, EVENTS_QUERY, POLICIES_QUERY } from './queries';
import PolicyEditor from './PolicyEditor';
import NightlyFxSection from './NightlyFxSection';
import { Code, CountCell, CsvExportButton, PercentCell, TableSkeleton, TomanCell, useEnumLabel } from './shared';

const STATUS_VARIANT = { draft: 'outline', active: 'success', archived: 'outline' };
const EVENTS_PAGE_SIZE = 20;

/**
 * View «سیاست‌ها» — the list, and at `?policy=<id|new>` the editor (§12.6).
 *
 * A policy is never edited after activation and never un-archived; the only
 * moves are «فعال‌سازی» on a draft and «کپی به پیش‌نویس» from anything. That is
 * what keeps history linear, so every historical charge keeps pointing at the
 * policy that priced it (flow F10).
 */
export default function PoliciesView({ window, view, go, onEditorDirty }) {
  const { t, formatDate, formatDateTime, formatDigits } = useTranslation();
  const toast = useToast();
  const statusLabel = useEnumLabel('aiPricing.policies.status');
  const basisLabel = useEnumLabel('aiPricing.policies.editor.basisOptions');
  const kindLabel = useEnumLabel('aiPricing.policies.events.kind');
  const fxSourceLabel = useEnumLabel('aiPricing.policies.fxSource');

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [source, setSource] = React.useState('');
  const [archiving, setArchiving] = React.useState(null);
  const [pending, setPending] = React.useState(false);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(POLICIES_QUERY)
      .then((d) => setRows(d?.aiPricingPolicies || []))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [t]);

  React.useEffect(() => { load(); }, [load]);

  const active = rows.find((p) => p.status === 'active') || null;

  const archive = async () => {
    setPending(true);
    try {
      await gql(ARCHIVE_POLICY, { id: archiving.id });
      toast.success(t('aiPricing.policies.archiveDialog.success'));
      setArchiving(null);
      load({ quiet: true });
    } catch (e) {
      toast.error(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  if (view.policy) {
    return (
      <PolicyEditor
        key={view.policy}
        policyId={view.policy}
        cloneFromId={view.from || null}
        policies={rows}
        onExit={() => go({ policy: '', from: '' })}
        onDirtyChange={onEditorDirty}
        onActivated={() => { go({ policy: '', from: '' }); load({ quiet: true }); }}
        onSaved={(id) => { if (view.policy === 'new' && id) go({ policy: id, from: '' }); }}
        onOpenPolicy={(id) => go({ policy: String(id), from: '' })}
      />
    );
  }

  // The USD rate exists only for a credit priced in toman (PM-0211). A list with
  // no such policy — a USD deployment — has no rate column at all; a mixed one
  // shows the dash on the rows that never had a rate.
  const withFx = !rows.length || rows.some((p) => isIrtCredit(p.creditCurrency));

  const csvColumns = [
    { key: 'id', header: 'id', value: (p) => p.id },
    { key: 'name', header: t('aiPricing.policies.columns.name'), value: (p) => p.name },
    { key: 'status', header: t('aiPricing.policies.columns.status'), value: (p) => p.status },
    { key: 'fxSource', header: t('aiPricing.policies.columns.fxSource'), value: (p) => p.fxSource || 'manual' },
    { key: 'basePolicyId', header: t('aiPricing.policies.columns.base'), value: (p) => p.basePolicyId },
    withFx ? { key: 'fx', header: t('aiPricing.policies.columns.fx'), value: (p) => p.fxTomanPerUsd } : null,
    { key: 'margin', header: t('aiPricing.policies.columns.margin'), value: (p) => p.targetMarginPct },
    { key: 'basis', header: t('aiPricing.policies.editor.basis'), value: (p) => p.costBasis },
    { key: 'window', header: t('aiPricing.policies.editor.window'), value: (p) => p.windowDays },
    { key: 'priced', header: t('aiPricing.policies.columns.priced'), value: (p) => p.servicesPriced },
    { key: 'activatedAt', header: t('aiPricing.policies.columns.activated'), value: (p) => p.activatedAt },
  ].filter(Boolean);

  const columns = [
    {
      key: 'name',
      header: t('aiPricing.policies.columns.name'),
      render: (p) => {
        // PM-0427: who set this policy's dollar — an operator, or the nightly
        // dollar, which derives its prices from an operator's policy (the link).
        const source = p.fxSource || 'manual';
        return (
          <div className="min-w-0">
            <div dir="auto" className="truncate font-semibold text-foreground">{p.name}</div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <span>{t('aiPricing.services.source.policy', { id: formatDigits(p.id) })}</span>
              <Badge variant={source === 'nightly' ? 'default' : 'outline'} data-testid={`ai-policies-row-${p.id}-fx-source`} data-source={source}>
                {fxSourceLabel(source)}
              </Badge>
              {p.basePolicyId ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={(e) => { e.stopPropagation(); go({ policy: String(p.basePolicyId) }); }}
                  data-testid={`ai-policies-row-${p.id}-base`}
                >
                  {t('aiPricing.policies.basedOn', { id: formatDigits(p.basePolicyId) })}
                </Button>
              ) : null}
            </div>
          </div>
        );
      },
    },
    { key: 'status', header: t('aiPricing.policies.columns.status'), width: '7rem', render: (p) => <Badge variant={STATUS_VARIANT[p.status] || 'outline'}>{statusLabel(p.status)}</Badge> },
    withFx ? {
      key: 'fx',
      header: t('aiPricing.policies.columns.fx'),
      align: 'end',
      width: '8rem',
      render: (p) => (isIrtCredit(p.creditCurrency)
        ? <span><TomanCell value={p.fxTomanPerUsd} /> {t('common.units.toman')}</span>
        : EMPTY),
    } : null,
    { key: 'margin', header: t('aiPricing.policies.columns.margin'), align: 'end', width: '7rem', render: (p) => <PercentCell value={p.targetMarginPct} /> },
    { key: 'basis', header: t('aiPricing.policies.columns.basis'), width: '9rem', render: (p) => t('aiPricing.policies.basisWindow', { basis: basisLabel(p.costBasis), days: p.windowDays }) },
    {
      key: 'priced',
      header: t('aiPricing.policies.columns.priced'),
      align: 'end',
      width: '8rem',
      render: (p) => (
        <span className="inline-flex items-center gap-1">
          <CountCell value={p.servicesPriced} />
          {num(p.negativeMargins) > 0 ? <Badge variant="destructive" className="tabular-nums">{formatDigits(p.negativeMargins)}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'activated',
      header: t('aiPricing.policies.columns.activated'),
      width: '13rem',
      render: (p) => (p.activatedAt ? (
        <span className="inline-flex flex-wrap items-center gap-1">
          {formatDate(p.activatedAt)}
          {p.activatedBy ? <ActorLabel actor={p.activatedBy} /> : null}
        </span>
      ) : EMPTY),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('aiPricing.policies.columns.actions')}</span>,
      align: 'end',
      width: '18rem',
      render: (p) => (
        <span className="inline-flex flex-wrap items-center justify-end gap-1">
          {p.status === 'draft' ? (
            <Button size="sm" onClick={(e) => { e.stopPropagation(); go({ policy: p.id }); }} data-testid={`ai-policies-row-${p.id}-activate`}>
              <Zap size={14} aria-hidden />
              {t('aiPricing.policies.actions.activate')}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); go({ policy: 'new', from: p.id }); }} data-testid={`ai-policies-row-${p.id}-clone`}>
            <Copy size={14} aria-hidden />
            {t('aiPricing.policies.actions.clone')}
          </Button>
          {p.status === 'draft' ? (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setArchiving(p); }} data-testid={`ai-policies-row-${p.id}-archive`}>
              <Archive size={14} aria-hidden />
              {t('aiPricing.policies.actions.archive')}
            </Button>
          ) : null}
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <div data-testid="ai-policies">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" aria-live="polite" data-testid="ai-policies-count">
          {t('aiPricing.policies.count', { count: rows.length })}
        </p>
        <div className="flex items-center gap-2">
          <CsvExportButton columns={csvColumns} rows={rows} data-testid="ai-policies-csv" />
          <Button onClick={() => { setSource(active ? active.id : ''); setCreating(true); }} data-testid="ai-policies-new">
            <Plus size={16} aria-hidden />
            {t('aiPricing.policies.new')}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card transition-opacity" style={{ opacity: refreshing ? 0.45 : 1 }} aria-busy={refreshing || undefined}>
        {loading ? (
          <TableSkeleton cols={8} rows={4} label={t('aiPricing.policies.loading')} data-testid="ai-policies-loading" />
        ) : error ? (
          <ErrorState
            title={t('aiPricing.policies.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-policies-error"
          />
        ) : (
          <Table
            dense
            ariaLabel={t('aiPricing.policies.tableAria')}
            columns={columns}
            rows={rows}
            rowKey={(p) => p.id}
            onRowActivate={(p) => go({ policy: p.id })}
            rowProps={(p) => ({ 'data-testid': `ai-policies-row-${p.id}`, 'aria-label': `${t('aiPricing.policies.actions.open')}: ${p.name}` })}
            empty={(
              <EmptyState
                icon={<FileText size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.policies.empty.title')}
                description={t('aiPricing.policies.empty.desc')}
                action={<Button onClick={() => { setSource(''); setCreating(true); }}>{t('aiPricing.policies.new')}</Button>}
                data-testid="ai-policies-empty"
              />
            )}
            data-testid="ai-policies-table"
          />
        )}
      </div>

      {/* The nightly dollar prices a credit sold in toman; a deployment whose
          credit is not (PM-0211) has no USD rate to follow. Which one this is,
          the policies say — so the section waits for them. */}
      {!loading && withFx ? <NightlyFxSection policies={rows} window={window} go={go} /> : null}

      <EventsSection kindLabel={kindLabel} formatDateTime={formatDateTime} />

      {/* «سیاست جدید»: from the active policy (the common case) or blank. */}
      <TaskDialog
        open={creating}
        onOpenChange={setCreating}
        title={t('aiPricing.policies.newDialog.title')}
        submitLabel={t('aiPricing.policies.newDialog.submit')}
        onSubmit={() => { setCreating(false); go({ policy: 'new', from: source || '' }); }}
        data-testid="ai-policies-new-dialog"
      >
        <div data-testid="ai-policies-new-source">
          <Select
            label={t('aiPricing.policies.newDialog.source')}
            value={source}
            onChange={(e) => setSource(e.target?.value ?? e)}
            options={[
              { value: '', label: t('aiPricing.policies.newDialog.blank') },
              ...rows.map((p) => ({ value: p.id, label: t('aiPricing.policies.newDialog.fromPolicy', { name: p.name }) })),
            ]}
            placeholder={t('aiPricing.policies.newDialog.source')}
          />
        </div>
      </TaskDialog>

      <ConfirmDialog
        open={!!archiving}
        onOpenChange={(open) => { if (!open && !pending) setArchiving(null); }}
        title={t('aiPricing.policies.archiveDialog.title', { name: archiving?.name || '' })}
        description={t('aiPricing.policies.archiveDialog.body')}
        confirmLabel={t('aiPricing.policies.archiveDialog.confirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        loading={pending}
        onConfirm={archive}
        data-testid="ai-policies-archive-confirm"
        confirmTestId="ai-policies-archive-confirm-submit"
      />
    </div>
  );
}

/** «تاریخچهٔ تغییرات» — the audit trail (flow F16), server-paged. */
function EventsSection({ kindLabel, formatDateTime }) {
  const { t, formatNumber } = useTranslation();
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(EVENTS_QUERY, { page, pageSize: EVENTS_PAGE_SIZE })
      .then((d) => setData(d?.aiPricingEvents || { events: [], total: 0 }))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [page, t]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-6" data-testid="ai-policies-events">
      <h2 className="mb-2 inline-flex items-center gap-1.5 text-sm font-bold text-foreground">
        <History size={16} aria-hidden />
        {t('aiPricing.policies.events.title')}
      </h2>
      <div className="rounded-lg border border-border bg-card">
        {loading ? (
          <TableSkeleton cols={4} rows={3} label={t('aiPricing.policies.events.loading')} data-testid="ai-policies-events-loading" />
        ) : error ? (
          <ErrorState
            title={t('aiPricing.policies.events.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-policies-events-error"
          />
        ) : (
          <Table
            dense
            ariaLabel={t('aiPricing.policies.events.aria')}
            rows={data?.events || []}
            rowKey={(e) => e.id}
            columns={[
              { key: 'at', header: t('aiPricing.policies.events.columns.at'), width: '11rem', render: (e) => formatDateTime(e.at) },
              { key: 'actor', header: t('aiPricing.policies.events.columns.actor'), width: '10rem', render: (e) => <ActorLabel actor={e.actorSub} /> },
              { key: 'kind', header: t('aiPricing.policies.events.columns.kind'), width: '12rem', render: (e) => <Badge variant="outline">{kindLabel(e.kind)}</Badge> },
              { key: 'entity', header: t('aiPricing.policies.events.columns.entity'), render: (e) => <Code>{`${e.entity}/${e.entityId}`}</Code> },
            ]}
            empty={<p className="p-4 text-sm text-muted-foreground" data-testid="ai-policies-events-empty">{t('aiPricing.policies.events.empty')}</p>}
            data-testid="ai-policies-events-table"
          />
        )}
      </div>
      {!loading && !error ? (
        <div data-testid="ai-policies-events-pager">
          <Pager
            page={page}
            pageCount={pageCountOf(data?.total, EVENTS_PAGE_SIZE)}
            pageSize={EVENTS_PAGE_SIZE}
            onChange={setPage}
            prevLabel={t('common.pager.prev')}
            nextLabel={t('common.pager.next')}
            pageLabel={(p, total) => t('common.pager.label', { page: p, total })}
            formatNumber={formatNumber}
          />
        </div>
      ) : null}
    </section>
  );
}
